import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { IMAGE_URL } from "network";
import { useEffect, useState } from "react";
import { useGetCategoriesQuery } from "slices/beautyApp/categorySlice";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

function AddForm({ open, onClose, initialData, onSubmit }) {
  const { data, isLoading, refetch } = useGetCategoriesQuery({});

  const [formData, setFormData] = useState({
    category: "",
    name: "",
    image: null,
    metaTitle: "",
    metaDescription: "",
    keywords: [], // <-- add keywords array here
    description: "",
  });
  const [previewUrl, setPreviewUrl] = useState("");
  const [keywordInput, setKeywordInput] = useState(""); // for the new keyword input

  useEffect(() => {
    if (initialData) {
      setFormData({
        _id: initialData._id || "",
        category: initialData.category || "",
        name: initialData.name || "",
        image: initialData.image || null,
        metaTitle: initialData.metaTitle || "",
        metaDescription: initialData.metaDescription || "",
        keywords: initialData.keywords || [],
        description: initialData.description || "", // ✅ FIX
      });

      if (initialData.image) {
        setPreviewUrl(IMAGE_URL + initialData.image);
      }
    } else {
      setPreviewUrl("");
    }
  }, [initialData]);

  const handleClose = () => {
    onClose();
    setFormData({
      category: "",
      name: "",
      image: null,
      metaTitle: "",
      metaDescription: "",
      keywords: [],
      description: "",
    });
  };

  useEffect(() => {
    if (!open) {
      setFormData({
        category: "",
        name: "",
        image: null,
        metaTitle: "",
        metaDescription: "",
        keywords: [],
        description: "",
      });
      setPreviewUrl("");
      setKeywordInput("");
    }
  }, [open]);

  const handleSubmit = async () => {
    await onSubmit(formData);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // If file input changed
    if (field === "image" && value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
    }
  };

  const addKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !formData.keywords.includes(trimmed)) {
      setFormData((prev) => ({
        ...prev,
        keywords: [...prev.keywords, trimmed],
      }));
      setKeywordInput("");
    }
  };

  // Remove keyword by value
  const removeKeyword = (keywordToRemove) => {
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((k) => k !== keywordToRemove),
    }));
  };

  // Handle Enter key press inside keyword input
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addKeyword();
    }
  };

  // console.log("data", data);
  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle> {initialData ? "Edit" : "Add"} </DialogTitle>
      <DialogContent dividers>
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="category-label">Category</InputLabel>
          <Select
            labelId="category-label"
            value={formData.category || ""}
            label="Category"
            onChange={(e) => handleChange("category", e.target.value)}
          >
            {data?.data && data.data.length > 0 ? (
              data.data.map((item) => (
                <MenuItem key={item._id} value={item._id}>
                  {item.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No categories found</MenuItem>
            )}
          </Select>
        </FormControl>
        <TextField
          label="Category Name"
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <input
          type="file"
          accept="image/*"
          onChange={(e) => handleChange("image", e.target.files[0])}
          style={{ marginTop: "16px" }}
          required={!initialData?.image}
        />
        {previewUrl && (
          <img
            crossorigin="anonymous"
            src={previewUrl}
            alt="Preview"
            style={{
              marginTop: 16,
              width: "100px",
              maxHeight: 200,
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        )}

        {/* Description */}
        <FormControl fullWidth margin="normal" required>
          <FormLabel>Description</FormLabel>
          <CKEditor
            editor={ClassicEditor}
            data={formData.description || ""} // ✅ safety guard
            config={{
              toolbar: [
                "heading",
                "|",
                "bold",
                "italic",
                "underline",
                "|",
                "bulletedList",
                "numberedList",
                "|",
                "insertTable",
                "link",
                "imageUpload",
                "|",
                "undo",
                "redo",
              ],
              table: {
                contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
              },
            }}
            onChange={(event, editor) => {
              handleChange("description", editor.getData());
            }}
          />
        </FormControl>

        <TextField
          label="Meta Title"
          value={formData.metaTitle}
          onChange={(e) => handleChange("metaTitle", e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Meta Description"
          value={formData.metaDescription}
          onChange={(e) => handleChange("metaDescription", e.target.value)}
          fullWidth
          margin="normal"
          required
        />

        {/* Keywords input and add button */}
        <Box display="flex" alignItems="center" mt={2} mb={1} gap={1}>
          <TextField
            label="Add Keyword"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={handleKeyDown}
            fullWidth
          />
          <Button onClick={addKeyword} variant="contained" color="primary">
            Add
          </Button>
        </Box>

        {/* Show keywords as Chips */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {formData?.keywords?.map((keyword) => (
            <Chip
              key={keyword}
              label={keyword}
              onDelete={() => removeKeyword(keyword)}
              color="primary"
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          {initialData ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddForm;
